import React from 'react';
import {
  Chip, TextField, IconButton, CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';


type AutocompleteComponentType = {
  options: any[];
  value: any;
  handleChange: Function;
  isMulti?: boolean;
  label: string;
  disabled?: boolean;
  error?: boolean;
  radioState?: boolean;
  setRadioState?: Function;
  grouped?: boolean; // <-- New optional prop
  // Optional async/server-search support (e.g. search-as-you-type pickers backed by a
  // typeahead endpoint). All of these are opt-in and default to the previous static,
  // string-options behavior when omitted.
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  onInputChange?: (event: any, value: string, reason: string) => void;
  loading?: boolean;
  filterOptions?: (options: any[], state: any) => any[]; // pass (options) => options to disable client-side filtering of server-searched options
  noOptionsText?: string;
  placeholder?: string;
};

function AutocompleteComponent({
  options,
  value,
  handleChange,
  isMulti = false,
  label,
  disabled,
  error,
  radioState = undefined,
  setRadioState = undefined,
  grouped = false,
  getOptionLabel,
  isOptionEqualToValue,
  onInputChange,
  loading = false,
  filterOptions,
  noOptionsText,
  placeholder,
}: AutocompleteComponentType) {
  const safeValue = isMulti ? (Array.isArray(value) ? value : []) : value;
  const resolvedGetOptionLabel = getOptionLabel ?? (grouped ? (option: any) => option.key : undefined);

  return (
    <Autocomplete
      style={{ width: '100%' }}
      multiple={isMulti}
      options={grouped || getOptionLabel ? options : options.map(option => option || '')}
      getOptionLabel={resolvedGetOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      groupBy={grouped ? (option) => option.group || '' : undefined}
      value={safeValue}
      readOnly={disabled}
      loading={loading}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      onChange={(event, newValue) => {
        handleChange(event, newValue || (isMulti ? [] : ''));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          error={error}
          placeholder={placeholder}
          InputLabelProps={{
            shrink: true,
            sx: {
              color: 'primary',
              "&.Mui-focused": {
                color: 'primary'
              }
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
                {radioState !== undefined && setRadioState ? (
                  <IconButton onClick={() => setRadioState(!radioState)} size="small">
                    {radioState ? "AND" : "OR"}
                  </IconButton>
                ) : null}
              </>
            ),
          }}
        />
      )}
      renderTags={isMulti ? (value, getTagProps) =>
        value.map((option, index) => (
          option !== '' ? <Chip variant="outlined" label={resolvedGetOptionLabel ? resolvedGetOptionLabel(option) : (option.key ?? option)} {...getTagProps({ index })} /> : null
        )) : undefined
      }
      clearOnEscape
    />
  );
}

export default AutocompleteComponent;
